import type { AxiosResponse } from "axios";
import ApiClient from "src/utils/ApiClient";
import __ from "src/utils/Translations";
import { RECHARGE_PLANNING_SYSTEM_SERVICE } from "src/utils/Api";
import { getErrorMessage } from "src/utils/constants/apiErrors";
import newRelicErrorReport from "src/utils/newRelic/newRelicErrorReport";
import {
  AccountOwnerOperation,
  ChargeTemplateResponse,
  CreateCyclicChargePayload,
  CreateCyclicResetPayload,
  CreateOneTimeChargeTemplateWithFilePayload,
  CreateOneTimeChargeTemplateWithGroupsPayload,
  CreateOneTimeResetPayload,
  CsvFileContent,
  CyclicChargeCandidate,
  CyclicChargeDefinitionChangeDetailsResponse,
  CyclicChargeDefinitionWithEtag,
  CyclicChargeHistoryEntryResponse,
  CyclicChargeHistoryQuery,
  GetCyclicChargeDefinitionResponse,
  GetOneTimeChargeDefinitionWithSourcesResponse,
  OneTimeChargeDefinitionUpdatedResponse,
  PageResponse,
  UpdateCyclicChargePayload,
  UpdateCyclicChargeResponse,
  UpdateOneTimeChargePayload,
  UpdateOneTimeChargeTemplateResponse,
  WithEtag,
} from "./rechargePlanningSystem.models";
import {
  CHARGE_DEFINITION_REPORT_PARAMETERS_TYPE,
  CHARGE_DEFINITION_REPORT_POLICY,
  CreateFileReportResponse,
  FileReportResponse,
  REPORT_FORMATS,
  REPORT_PARAMETERS_TYPE,
  REPORT_POLICY,
  ReportFormat,
  REPORTS_PATH,
  ReportScope,
} from "./fileReport.models";
import {
  RechargePlanningSystemError,
  RpsErrorItem,
  RpsErrorRow,
} from "./rechargePlanningSystem.errors";

const SERVICE_NAME = "recharge-planning-system";

/**
 * Base URL shared by every RPS request. Built from the service constant so the
 * client and any endpoint facade cannot drift apart.
 */
export const RECHARGE_PLANNING_SYSTEM_BASE_URL = `/api/${RECHARGE_PLANNING_SYSTEM_SERVICE}/api/rest`;

/**
 * Backend zwraca roczną cykliczność jako "P1Y", a widoki/opcje znają tylko
 * równoważne "P12M". Ujednolicamy przy odczycie, żeby etykieta cykliczności
 * wyświetlała się poprawnie zamiast surowego "P1Y".
 */
const normalizePeriod = (period: string): string =>
  period === "P1Y" ? "P12M" : period;

const normalizeCandidate = <T extends { period: string }>(data: T): T => ({
  ...data,
  period: normalizePeriod(data.period),
});

export const RechargePlanningSystemApiClient = new ApiClient({
  baseURL: RECHARGE_PLANNING_SYSTEM_BASE_URL,
}).client;

RechargePlanningSystemApiClient.interceptors.response.use(undefined, (err) => {
  try {
    const errors = err.response?.data?.errors;
    if (errors?.length) {
      // One apiErrors.js lookup per backend error; the message, the code list
      // and the CSV rows are all read from these items.
      const items: RpsErrorItem[] = errors.map(
        ({ code, details }: { code: string; details?: object }) => {
          const resolved: { errorMessage?: string; details?: RpsErrorRow } =
            getErrorMessage(SERVICE_NAME, code, details);
          return {
            code,
            message: resolved.errorMessage,
            row: resolved.details,
          };
        }
      );
      const mapped = items.flatMap(({ message }) => (message ? [message] : []));
      if (mapped.length) {
        return Promise.reject(
          new RechargePlanningSystemError(
            __(mapped.join("; ")),
            items.map(({ code }) => code),
            err.response?.status,
            items
          )
        );
      }
    }
  } catch (e) {
    newRelicErrorReport(e, "rechargePlanningSystem/clients.ts - interceptor");
  }
  return Promise.reject(err);
});

// ---------------------------------------------------------------------------
// Employees (Account Owners)
// ---------------------------------------------------------------------------

export interface EmployeeDto {
  accountOwnerId: string;
  firstName: string;
  lastName: string;
  fkNumber: string;
  active: boolean;
}

export interface PaginatedEmployees {
  data: EmployeeDto[];
  count: number;
}

export interface FetchAccountOwnersParams {
  companyId: string;
  page: number;
  size: number;
  filters?: Record<string, string>;
  sort?: Array<{ key: string; value: string }> | null;
}

export const fetchAccountOwners = async (
  params: FetchAccountOwnersParams,
  signal?: AbortSignal
): Promise<PaginatedEmployees> => {
  const { companyId, page, size, filters = {}, sort } = params;

  const queryParams: Record<string, string | number> = {
    page,
    size,
    companyId,
    ...filters,
  };

  if (sort) {
    sort.forEach(({ key, value }) => {
      queryParams[`order[${key}]`] = value;
    });
  }

  const { data } = await RechargePlanningSystemApiClient.get(
    "/v1/private/account-owners",
    {
      params: queryParams,
      paramsSerializer: (params) => {
        const searchParams = new URLSearchParams();
        Object.entries(params as Record<string, string | number>).forEach(
          ([key, value]) => {
            searchParams.append(key, String(value));
          }
        );
        return searchParams.toString();
      },
      signal,
    }
  );

  const response = data as Record<string, unknown>;
  return {
    data: (response.content ?? []) as EmployeeDto[],
    count: (response.totalElements ?? 0) as number,
  };
};

// ---------------------------------------------------------------------------
// Employee Operations
// ---------------------------------------------------------------------------

export const fetchEmployeeOperations = async (
  accountOwnerId: string,
  signal?: AbortSignal
): Promise<AccountOwnerOperation[]> => {
  const { data } = await RechargePlanningSystemApiClient.get<
    AccountOwnerOperation[]
  >(`/v1/private/account-owner/${accountOwnerId}`, { signal });
  return (data as AccountOwnerOperation[]).map((operation) =>
    operation.cyclicChargeCandidate
      ? {
          ...operation,
          cyclicChargeCandidate: normalizeCandidate(
            operation.cyclicChargeCandidate
          ),
        }
      : operation
  );
};

export const deleteIndividualCharge = async (
  chargeDefinitionId: string,
  accountOwnerId: string,
  pointsBankId: string
): Promise<CyclicChargeCandidate | null> => {
  const { data } = await RechargePlanningSystemApiClient.post(
    `/v2/charges/cyclic/${chargeDefinitionId}/remove-individual-charges`,
    { accountOwnerId, pointsBankId }
  );
  return toNullableCandidate(data);
};

export const setIndividualCharge = async (
  chargeDefinitionId: string,
  accountOwnerId: string,
  pointsBankId: string,
  amount: number
): Promise<CyclicChargeCandidate | null> => {
  const { data } = await RechargePlanningSystemApiClient.post(
    `/v2/charges/cyclic/${chargeDefinitionId}/add-individual-charges`,
    { amount, accountOwnerId, pointsBankId }
  );
  return toNullableCandidate(data);
};

const toNullableCandidate = (data: unknown): CyclicChargeCandidate | null => {
  if (!data || typeof data !== "object") return null;
  if (Object.values(data).every((v) => v == null)) return null;
  return normalizeCandidate(data as CyclicChargeCandidate);
};

export const createCyclicReset = async (
  payload: CreateCyclicResetPayload
): Promise<void> => {
  await RechargePlanningSystemApiClient.post("/v2/resets/cyclic", payload);
};

export const createOneTimeReset = async (
  payload: CreateOneTimeResetPayload
): Promise<void> => {
  await RechargePlanningSystemApiClient.post("/v2/resets/one-time", payload);
};

export const createCyclicCharge = async (
  payload: CreateCyclicChargePayload,
  file?: Blob
): Promise<void> => {
  const formData = new FormData();
  formData.append(
    "request",
    new Blob([JSON.stringify(payload)], { type: "application/json" })
  );
  if (file) {
    formData.append("file", file, "individual_charges.csv");
  }
  await RechargePlanningSystemApiClient.post(
    "/v2/charges/cyclic/create-with-groups",
    formData
  );
};

export const fetchCyclicChargeDefinition = async (
  chargeDefinitionId: string,
  signal?: AbortSignal
): Promise<GetCyclicChargeDefinitionResponse> => {
  const { data } =
    await RechargePlanningSystemApiClient.get<GetCyclicChargeDefinitionResponse>(
      `/v2/charges/cyclic/${chargeDefinitionId}`,
      { signal }
    );
  return normalizeCandidate(data);
};

const ETAG_WEAK_PREFIX = /^W\//;
const ETAG_QUOTES = /^"(.*)"$/;

/**
 * The backend parses If-Match as a bare integer, while an HTTP ETag header is
 * quoted (`"3"`, possibly weak `W/"3"`). Strip both so the version round-trips.
 */
export const toIfMatchVersion = (etag: string): string =>
  etag.trim().replace(ETAG_WEAK_PREFIX, "").replace(ETAG_QUOTES, "$1");

/** Contract: GET /v2/charges/cyclic/{id} – definition as stored plus its version (ETag). */
export const fetchCyclicChargeDefinitionWithEtag = async (
  chargeDefinitionId: string,
  signal?: AbortSignal
): Promise<CyclicChargeDefinitionWithEtag> => {
  const { data, headers } =
    await RechargePlanningSystemApiClient.get<GetCyclicChargeDefinitionResponse>(
      `/v2/charges/cyclic/${chargeDefinitionId}`,
      { signal }
    );
  const etag = headers?.etag;
  if (!etag) {
    throw new Error(`Missing ETag for cyclic charge ${chargeDefinitionId}`);
  }
  return { definition: data, etag: toIfMatchVersion(String(etag)) };
};

// Contract: POST /api/recharge-planning-system/api/rest/v2/charges/cyclic/{chargeDefinitionId}/update
export const updateCyclicCharge = async (
  chargeDefinitionId: string,
  etag: string,
  payload: UpdateCyclicChargePayload,
  file?: Blob
): Promise<UpdateCyclicChargeResponse> => {
  const formData = new FormData();
  formData.append(
    "request",
    new Blob([JSON.stringify(payload)], { type: "application/json" })
  );
  if (file) {
    formData.append("file", file, "individual_charges.csv");
  }
  const { data } =
    await RechargePlanningSystemApiClient.post<UpdateCyclicChargeResponse>(
      `/v2/charges/cyclic/${chargeDefinitionId}/update`,
      formData,
      { headers: { "If-Match": etag } }
    );
  return data;
};

// Contract: DELETE /api/recharge-planning-system/api/rest/v2/charges/cyclic/{chargeDefinitionId}
export const cancelCyclicCharge = async (
  chargeDefinitionId: string
): Promise<void> => {
  await RechargePlanningSystemApiClient.delete(
    `/v2/charges/cyclic/${chargeDefinitionId}`
  );
};

/**
 * True when the points bank already has an active cyclic charge.
 * Uses the same endpoint as the listing which determines `hasActive` for
 * the "+ Dodaj doładowanie" button.
 *
 * Contract: GET /api/recharge-planning-system/api/rest/v2/charges/cyclic?pointsBankId=&status=ACTIVE
 */
export const hasActiveCyclicCharge = async (
  pointsBankId: string,
  signal?: AbortSignal
): Promise<boolean> => {
  const { data } = await RechargePlanningSystemApiClient.get<{
    content?: unknown[];
  }>("/v2/charges/cyclic", {
    params: { pointsBankId, status: "ACTIVE" },
    signal,
  });
  return Boolean(data?.content?.length);
};

// Contract: GET /api/recharge-planning-system/api/rest/v1/private/charges/cyclic/{chargeDefinitionId}/history
export const fetchCyclicChargeDefinitionHistory = async (
  chargeDefinitionId: string,
  { types, page, size, sort }: CyclicChargeHistoryQuery,
  signal?: AbortSignal
): Promise<PageResponse<CyclicChargeHistoryEntryResponse>> => {
  const { data } = await RechargePlanningSystemApiClient.get<
    PageResponse<CyclicChargeHistoryEntryResponse>
  >(`/v1/private/charges/cyclic/${chargeDefinitionId}/history`, {
    params: {
      type: types.length ? types : undefined,
      page,
      size,
      sort: sort ? `changedAt,${sort}` : undefined,
    },
    // The backend binds a Set from repeated keys (type=A&type=B), not type[]=A.
    paramsSerializer: { indexes: null },
    signal,
  });
  return data;
};

// Contract: GET /api/recharge-planning-system/api/rest/v1/private/charges/cyclic/{chargeDefinitionId}/history/{changeId}
export const fetchCyclicChargeDefinitionChangeDetails = async (
  chargeDefinitionId: string,
  changeId: string,
  signal?: AbortSignal
): Promise<CyclicChargeDefinitionChangeDetailsResponse> => {
  const { data } =
    await RechargePlanningSystemApiClient.get<CyclicChargeDefinitionChangeDetailsResponse>(
      `/v1/private/charges/cyclic/${chargeDefinitionId}/history/${changeId}`,
      { signal }
    );
  return data;
};

// Contract: DELETE /api/recharge-planning-system/api/rest/v2/charges/one-time/{chargeDefinitionId}
export const cancelOneTimeCharge = async (
  chargeDefinitionId: string
): Promise<void> => {
  await RechargePlanningSystemApiClient.delete(
    `/v2/charges/one-time/${chargeDefinitionId}`
  );
};

// Contract: POST /api/recharge-planning-system/api/rest/v2/charge-templates/one-time/{chargeTemplateId}/cancel
export const cancelOneTimeChargeTemplate = async (
  chargeTemplateId: string
): Promise<void> => {
  await RechargePlanningSystemApiClient.post(
    `/v2/charge-templates/one-time/${chargeTemplateId}/cancel`
  );
};

// Contract: POST /api/recharge-planning-system/api/rest/v2/charge-templates/one-time/{chargeTemplateId}/approve
export const approveOneTimeChargeTemplate = async (
  chargeTemplateId: string
): Promise<void> => {
  await RechargePlanningSystemApiClient.post(
    `/v2/charge-templates/one-time/${chargeTemplateId}/approve`
  );
};

// Contract: POST /api/recharge-planning-system/api/rest/v2/charge-templates/one-time/{chargeTemplateId}/reject
export const rejectOneTimeChargeTemplate = async (
  chargeTemplateId: string
): Promise<void> => {
  await RechargePlanningSystemApiClient.post(
    `/v2/charge-templates/one-time/${chargeTemplateId}/reject`
  );
};

// ---------------------------------------------------------------------------
// One-time charges / charge templates
// ---------------------------------------------------------------------------

// Contract: GET /api/recharge-planning-system/api/rest/v2/charges/one-time/{chargeDefinitionId}
export const fetchOneTimeCharge = async (
  chargeDefinitionId: string,
  signal?: AbortSignal
): Promise<GetOneTimeChargeDefinitionWithSourcesResponse> => {
  const { data } =
    await RechargePlanningSystemApiClient.get<GetOneTimeChargeDefinitionWithSourcesResponse>(
      `/v2/charges/one-time/${chargeDefinitionId}`,
      { signal }
    );
  return data;
};

// Contract: GET /api/recharge-planning-system/api/rest/v2/charge-templates/one-time/{chargeTemplateId}
export const fetchOneTimeChargeTemplate = async (
  chargeTemplateId: string,
  signal?: AbortSignal
): Promise<ChargeTemplateResponse> => {
  const { data } =
    await RechargePlanningSystemApiClient.get<ChargeTemplateResponse>(
      `/v2/charge-templates/one-time/${chargeTemplateId}`,
      { signal }
    );
  return data;
};

// ---------------------------------------------------------------------------
// One-time charges / charge templates – editing (ETag-based optimistic lock)
// ---------------------------------------------------------------------------

/** Bare version from the `ETag` header; null when the backend sent none. */
const readEtag = (headers: AxiosResponse["headers"]): string | null => {
  const etag = headers?.etag;
  return etag ? toIfMatchVersion(String(etag)) : null;
};

/** Multipart body with the JSON `request` part; the caller appends the `file` part. */
const toRequestFormData = (payload: object): FormData => {
  const formData = new FormData();
  formData.append(
    "request",
    new Blob([JSON.stringify(payload)], { type: "application/json" })
  );
  return formData;
};

// Contract: GET /api/recharge-planning-system/api/rest/v2/charges/one-time/{chargeDefinitionId}
export const fetchOneTimeChargeWithEtag = async (
  chargeDefinitionId: string,
  signal?: AbortSignal
): Promise<WithEtag<GetOneTimeChargeDefinitionWithSourcesResponse>> => {
  const { data, headers } =
    await RechargePlanningSystemApiClient.get<GetOneTimeChargeDefinitionWithSourcesResponse>(
      `/v2/charges/one-time/${chargeDefinitionId}`,
      { signal }
    );
  return { data, etag: readEtag(headers) };
};

// Contract: GET /api/recharge-planning-system/api/rest/v2/charge-templates/one-time/{chargeTemplateId}
export const fetchOneTimeChargeTemplateWithEtag = async (
  chargeTemplateId: string,
  signal?: AbortSignal
): Promise<WithEtag<ChargeTemplateResponse>> => {
  const { data, headers } =
    await RechargePlanningSystemApiClient.get<ChargeTemplateResponse>(
      `/v2/charge-templates/one-time/${chargeTemplateId}`,
      { signal }
    );
  return { data, etag: readEtag(headers) };
};

// Contract: POST /api/recharge-planning-system/api/rest/v2/charges/one-time/{chargeDefinitionId}/update
export const updateOneTimeCharge = async (
  chargeDefinitionId: string,
  etag: string,
  payload: UpdateOneTimeChargePayload,
  file?: Blob
): Promise<OneTimeChargeDefinitionUpdatedResponse> => {
  const formData = toRequestFormData(payload);
  if (file) {
    formData.append("file", file, "individual_charges.csv");
  }
  const { data } =
    await RechargePlanningSystemApiClient.post<OneTimeChargeDefinitionUpdatedResponse>(
      `/v2/charges/one-time/${chargeDefinitionId}/update`,
      formData,
      { headers: { "If-Match": etag } }
    );
  return data;
};

// Contract: POST /api/recharge-planning-system/api/rest/v2/charge-templates/one-time/{chargeTemplateId}/update
export const updateOneTimeChargeTemplate = async (
  chargeTemplateId: string,
  etag: string,
  payload: UpdateOneTimeChargePayload,
  file?: Blob
): Promise<UpdateOneTimeChargeTemplateResponse> => {
  const formData = toRequestFormData(payload);
  if (file) {
    formData.append("file", file, "individual_charges.csv");
  }
  const { data } =
    await RechargePlanningSystemApiClient.post<UpdateOneTimeChargeTemplateResponse>(
      `/v2/charge-templates/one-time/${chargeTemplateId}/update`,
      formData,
      { headers: { "If-Match": etag } }
    );
  return data;
};

// ---------------------------------------------------------------------------
// One-time charge templates – creating
// ---------------------------------------------------------------------------

// Contract: POST /api/recharge-planning-system/api/rest/v2/charge-templates/one-time/create-with-file
export const createOneTimeChargeTemplateWithFile = async (
  payload: CreateOneTimeChargeTemplateWithFilePayload,
  file: File
): Promise<void> => {
  const formData = toRequestFormData(payload);
  // No explicit name: the part keeps the uploaded file's own name.
  formData.append("file", file);
  await RechargePlanningSystemApiClient.post(
    "/v2/charge-templates/one-time/create-with-file",
    formData
  );
};

// Contract: POST /api/recharge-planning-system/api/rest/v2/charge-templates/one-time/create-with-groups
export const createOneTimeChargeTemplateWithGroups = async (
  payload: CreateOneTimeChargeTemplateWithGroupsPayload,
  file?: Blob
): Promise<void> => {
  const formData = toRequestFormData(payload);
  if (file) {
    formData.append("file", file, "individual_charges.csv");
  }
  await RechargePlanningSystemApiClient.post(
    "/v2/charge-templates/one-time/create-with-groups",
    formData
  );
};

// ---------------------------------------------------------------------------
// Charge CSV files
// ---------------------------------------------------------------------------

/**
 * Fetches a charge's individual-charges CSV. Returns null when the stored file
 * carries no payload.
 *
 * Contract: GET /api/recharge-planning-system/api/rest/v2/csv-files/{fileId}
 * (goes through the shared client so it inherits the `/api/rest` base every
 * other RPS endpoint uses - a dedicated client without that prefix hit the
 * bare service root and 404'd).
 */
export const fetchCsvFileContent = async (
  fileId: string,
  signal?: AbortSignal
): Promise<CsvFileContent | null> => {
  const { data } = await RechargePlanningSystemApiClient.get(
    `/v2/csv-files/${fileId}`,
    { signal }
  );

  const { fileData, fileName } = (data ?? {}) as Partial<CsvFileContent>;

  if (!fileData) return null;

  return {
    fileData,
    fileName: fileName || "individual_charges.csv",
  };
};

// ---------------------------------------------------------------------------
// File reports (cyclic individual charges)
// ---------------------------------------------------------------------------

const getReportRequest = (scope: ReportScope) =>
  scope.chargeDefinitionId
    ? {
        policy: CHARGE_DEFINITION_REPORT_POLICY,
        parameters: {
          type: CHARGE_DEFINITION_REPORT_PARAMETERS_TYPE,
          chargeDefinitionId: scope.chargeDefinitionId,
        },
      }
    : {
        policy: REPORT_POLICY,
        parameters: {
          type: REPORT_PARAMETERS_TYPE,
          companyId: scope.companyId,
        },
      };

/**
 * Requests a report to be generated and returns its id.
 *
 * Contract: POST /api/recharge-planning-system/api/rest/v1/private/reports
 * -> 202 { fileReportId }. A 4xx/5xx surfaces through the shared error
 * interceptor, so the id is always present on the resolved response.
 */
export const createFileReport = async (
  scope: ReportScope,
  format: ReportFormat,
  signal?: AbortSignal
): Promise<string> => {
  const { data } =
    await RechargePlanningSystemApiClient.post<CreateFileReportResponse>(
      REPORTS_PATH,
      getReportRequest(scope),
      {
        headers: {
          Accept: REPORT_FORMATS[format].mimeType,
          "Content-Type": "application/json",
        },
        signal,
      }
    );

  return data.fileReportId;
};

/**
 * Fetches the generated report as a blob. Returns the raw status so callers can
 * distinguish "ready" (200) from "still generating" (202) and drive polling.
 *
 * A 202 stays inside axios' default 2xx success range, so it resolves here
 * instead of throwing - the poller inspects `response.status` to decide whether
 * to retry. Setting a custom `validateStatus` that rejects 202 would silently
 * break polling.
 *
 * Contract: GET /api/recharge-planning-system/api/rest/v1/private/reports/{id}
 */
export const getFileReport = async (
  fileReportId: string,
  format: ReportFormat,
  signal?: AbortSignal
): Promise<FileReportResponse> => {
  const response = await RechargePlanningSystemApiClient.get<Blob>(
    `${REPORTS_PATH}/${encodeURIComponent(fileReportId)}`,
    {
      headers: {
        Accept: REPORT_FORMATS[format].mimeType,
      },
      responseType: "blob",
      signal,
    }
  );

  return {
    status: response.status,
    blob: response.data,
    headers: response.headers,
  };
};
